<template>

    <v-app>
        <title>{{appName}}</title>
        <div id="app">
            <ComponenteLoading :state="showLoadingTemplate"/>

            <!-- Template da tela de Login -->
            <template
                v-if="checkRouteName === 'login' || checkRouteName === 'resetPassword' || checkRouteName === 'confirmAccountTest'">
                <ComponenteLoading :state="showLoadingMain"/>
                <MainComponent/>
            </template>

            <!-- Template First Login -->
            <template
                v-if="checkRouteName !== 'login' && checkRouteName !== 'resetPassword' && checkRouteName !== 'confirmAccountTest' && !initialState.acceptedTerms">
                <header id="header">
                    <ComponenteLoading :state="showLoadingHeader"/>
                    <HeaderComponent/>
                </header>

                <div id="content" :class="!initialState.acceptedTerms ? 'terms-and-conditions-first-login' : ''">
                    <main id="main">
                        <ComponenteLoading :state="showLoadingMain"/>
                        <MainComponent/>
                    </main>
                </div>

                <footer id="footer">
                    <div></div>
                </footer>
            </template>

            <!-- Template do DataDrive -->
            <template v-else-if="showFrontOffice()">
                <header id="header" v-if="checkRouteName !== 'login'">
                    <ComponenteLoading :state="showLoadingHeader"/>
                    <HeaderComponent/>
                </header>

                <div v-if="checkRouteName !== 'login'" id="menu-sidebar" class="side-bar-menu-div"
                     :class="{'show': $store.state.showSideBar, 'collapsed': sidebarCollapsed}">
                    <ComponenteLoading :state="showLoadingSidebar"/>
                    <SidebarMenuComponent/>
                </div>

                <div id="content" :class="{'collapsed': sidebarCollapsed}">
                    <main id="main">
                        <ComponenteLoading :state="showLoadingMain"/>
                        <MainComponent/>
                    </main>
                </div>

                <footer id="footer" v-if="checkRouteName !== 'login'">
                    <ComponenteLoading :state="showLoadingFooter"/>
                    <FooterComponent/>
                </footer>
            </template>

            <!-- Template do Backoffice -->
            <template
                v-else-if="checkRouteModule && isReady && checkRouteName !== 'login' && checkRouteName !== 'resetPassword' && checkRouteName !== 'confirmAccountTest' && initialState.acceptedTerms">
                <div :key="backofficeKey">
                    <header id="header" v-if="checkRouteName !== 'login'">
                        <ComponenteLoading :state="showLoadingHeader"/>
                        <HeaderBackoffice/>
                    </header>

                    <div v-if="checkRouteName !== 'login'" id="menu-sidebar-backoffice" class="side-bar-backoffice-div"
                        :class="{'show': $store.state.showSideBar}">
                        <ComponenteLoading :state="showLoadingSidebar"/>
                        <SidebarBackoffice/>
                    </div>

                    <div id="content-backoffice" :class="{'show': $store.state.showSideBar}">
                        <main id="main">
                            <ComponenteLoading :state="showLoadingMain"/>
                            <MainComponent/>
                        </main>
                    </div>

                    <footer id="footer" v-if="checkRouteName !== 'login'">
                        <ComponenteLoading :state="showLoadingFooter"/>
                        <FooterComponent/>
                    </footer>
                </div>
            </template>

            <modal-changed-operation-to-shopping-cart-component/>
            <information-modal/>
            <modal-clutch-kit/>

            <datarepair-modal/>
            <webshop-modal/>

            <Chat v-if="showFrontOffice() && this.$route.name != 'chat'" class="chat-fab"/>

            <!-- Modal Check Budget before continue -->
            <button id="invisible-button-app" style="display: hidden" @click="checkClickedInvisibleBtn"></button>

            <base-modal v-model="modalAlertToLeave.show" :title="$t('heads-up')" modal-width="560px"
                        @watch-show-value="clickOutsideFunc">
                <template v-slot:modal-close>
                    <div @click="modalAlertToLeave.show = false">
                        <img src="/images/_SVGs/environmental/fechar.svg" onload="SVGInject(this)" width="20"
                             height="20"/>
                    </div>
                </template>

                <template v-slot:modal-body>
                    {{ $t('ask-save-budget-before-exit') }}
                </template>

                <template v-slot:modal-actions>
                    <div class="mr-2">
                        <v-btn text class="danger-button" @click="onDontSaveBudget">
                            <span style="text-transform: capitalize">{{ $t('backoffice.actions.cancel') }}</span>
                        </v-btn>
                    </div>
                    <div class="ml-2">
                        <v-btn text class="primary-button" @click="onSaveBudget">
                            <span style="text-transform: capitalize">{{ $t('backoffice.actions.finalize') }}</span>
                        </v-btn>
                    </div>
                </template>
            </base-modal>

            <base-snackbar
                v-model="snackbar.show"
                :message="snackbar.message"
                :type="snackbar.type"
                @watch-show-value="(val) => (snackbar.show = val)"
            />
        </div>
    </v-app>

</template>

<script>
import Vue from 'vue'
import Talk from "talkjs";
import pusherHub from '@plugins/pusherHub';
import mixinLogin from "@mixins/b2b/login.js";
import mixinMenus from "@mixins/b2b/menus.js";
import mixinNotifications from "@mixins/b2b/notifications.js";
import mixinTutorials from "@mixins/b2b/tutorials.js";
import BaseSnackbar from "@components/shared/widgets/BaseSnackbar.vue";
import BaseModal from "@components/shared/widgets/BaseModal.vue";
import postHog from "@plugins/postHog.js";
import dayjs from '@plugins/dayjs.js';

Vue.use(pusherHub);
Vue.use(postHog);
Vue.use(dayjs);

export default {
    name: 'App',
    components: {
        BaseModal,
        BaseSnackbar,
        HeaderComponent: () => import('@components/shared/_template/header/HeaderComponent'),
        HeaderBackoffice: () => import('@components/shared/_template/header/HeaderBackoffice'),
        SidebarMenuComponent: () => import('@components/shared/_template/sidebar/SidebarMenuComponent'),
        SidebarBackoffice: () => import('@components/backoffice/_template/sidebar/SidebarBackoffice'),
        MainComponent: () => import('@components/shared/_template/main/MainComponent'),
        FooterComponent: () => import('@components/shared/_template/footer/FooterComponent'),
        ComponenteLoading: () => import('@components/shared/widgets/LoadingComponent'),
        Chat: () => import('@components/shared/_template/header/chat/ChatComponent.vue'),
    },
    mixins: [
        mixinLogin,
        mixinMenus,
        mixinNotifications,
        mixinTutorials,
    ],
    data() {
        return {
            snackbar: {
                show: false,
                message: "",
                type: 'error'
            },
            initialState: '',
            appName: 'DataDrive',
            modalAlertToLeave: {
                show: false
            },
            isReady: false,
            backofficeKey: 0,
        };
    },
    computed: {
        userAuth() {
            return this.$store.getters["auth/getState"];
        },
        userToken() {
            return this.$store.getters["auth/getState"].token;
        },
        showLoadingSidebar() {
            return this.$store.getters['loadings/getLoadingState'].loadings.showLoadingSidebar;
        },
        showLoadingTemplate() {
            return this.$store.getters['loadings/getLoadingState'].loadings.showLoadingTemplate;
        },
        showLoadingHeader() {
            return this.$store.getters['loadings/getLoadingState'].loadings.showLoadingHeader;
        },
        showLoadingMain() {
            return this.$store.getters['loadings/getLoadingState'].loadings.showLoadingMain;
        },
        showLoadingFooter() {
            return this.$store.getters['loadings/getLoadingState'].loadings.showLoadingFooter;
        },
        checkRouteName() {
            return this.$route.name;
        },
        checkRouteModule() {
            const str = this.$route.fullPath;
            if(str !== '/') {
                this.isReady = true;
            }
            return str.indexOf("backoffice") != -1;
        },
        sidebarCollapsed() {
            return this.$store.state.sidebarCollapsed;
        },
        hasActiveRequestManagerModule() {
            const modules = this.$store.getters['auth/getState'].modules;
            const module24 = modules.find(m => m.moduleId === this.$globals.MODULES.REQUEST_MANAGER.ID);
            return module24 && module24.isActive && module24.isVisible;
        },
    },
    methods: {
        showFrontOffice() {
            return !this.checkRouteModule &&
                this.isReady &&
                this.checkRouteName !== 'login' &&
                this.checkRouteName !== 'resetPassword'
                && this.checkRouteName !== 'confirmAccountTest'
                && this.initialState.acceptedTerms
        },
        async initTalkjsSession() {
            await Talk.ready;
            const me = new Talk.User({
                id: this.userAuth.userId,
                name: this.userAuth.fullName,
                email: this.userAuth.email,
                photoUrl: this.userAuth.photo,
                role: "default",
            });

            const talkSession = new Talk.Session({
                appId: process.env.VUE_APP_TALKJS_APP_ID,
                me: me,
            });

            this.$store.commit('talkjs/setSession', talkSession);
        },
        checkClickedInvisibleBtn() {
            this.modalAlertToLeave.show = true;
        },
        onDontSaveBudget() {
            this.modalAlertToLeave.show = false;
            const checkClickedRoute = this.$store.getters['sidebarActiveMenu/getStateSidebarActiveMenu'].routeSidebar
            if(checkClickedRoute === '/') {
                localStorage.clear();
                this.$cookies.remove('token', { secure: true, expires: 1 });
                this.$store.commit('auth/setState', {});
                this.$store.commit('vehicleIdentification/setVehicleIdentificationClear');
                this.$router.push('/');
                location.reload();
            } else {
                document.location = checkClickedRoute;
            }
        },
        onSaveBudget() {
            this.modalAlertToLeave.show = false;
            this.$router.push({ name: 'budget-summary' });
        },
        onHandleCloseModal(param) {
            this.modalSearchVehicle.show = !param;
        },
        clickOutsideFunc(value) {
            this.modalAlertToLeave.show = value;
        },
        initPusherAndBeams() {
            this.$initChannels({
                loginInternalDetails: this.getLoginInternalDetails,
                refreshModulesNotifications:
                    this.refreshModulesNotifications,
                refreshRequestManagerNotifications:
                    this.refreshRequestManagerNotifications,
                getGarageIndicators: this.getGarageIndicators,
                getNotifications: this.getNotifications,
                showSnackbar: this.showSnackbar,
                QuotationHistory: this.getsQuotationHistory,
            });
        },
        showSnackbar(isError, message) {
            this.snackbar.message = message;
            this.snackbar.type = isError ? "error" : "green";
            this.snackbar.show = true;
        },
        getsQuotationHistory() {
            this.$root.$emit('refreshQuotationHistory');
        },
    },
    watch: {
        hasActiveRequestManagerModule(newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.$initBeams();
            } else {
                this.$closeBeams();
            }
        },
        userAuth: {
            handler: async function(newValue, oldValue) {
                if(this.userAuth && this.userAuth.userId) {
                    this.initTalkjsSession();
                }
            },
            deep: true,
        },
        userToken: {
            handler(newToken, oldToken) {
                if (newToken && newToken !== oldToken) {
                    this.initPusherAndBeams();
                    this.backofficeKey += 1;
                }
            },
            immediate: true,
        },
        checkRouteName: {
            handler: function(newValue, oldValue) {
                return newValue;
            },
            immediate: true,
            deep: true,
        },
        initialState: {
            handler: function(newValue, oldValue) {
                return newValue;
            },
            immediate: true,
            deep: true,
        },
        $route (to, from){
            const searchString = '/tech-info';
            if (from.path.includes(searchString)) {
                this.$store.commit('techInfo/setWatchTechInfoFromRoute', true);
            } else {
                this.$store.commit('techInfo/setWatchTechInfoFromRoute', false);
            }
        },
    },
    created() {
        const initialState =  JSON.parse(localStorage.getItem('@datadrive:initialState'));
        const domain =  JSON.parse(localStorage.getItem('@datadrive:domain'));
        if(!initialState) {
            localStorage.removeItem('@datadrive:initialState');
            localStorage.removeItem('@datadrive:token');
            localStorage.removeItem('@datadrive:refreshToken');

            this.$cookies.remove('token', { secure: true, expires: 1 });
            this.$store.commit('auth/setState', {});
        } else {
            const userToken = this.$cookies.get('token');
            if(userToken) {
                const payloadToSaveOnState = {
                    token: userToken,
                    ...initialState
                }
                this.$store.commit("auth/setState", payloadToSaveOnState);
                this.getLoginInternalDetails(
                    userToken,
                    initialState.cultureName,
                    true,
                );
            } else {
                this.$store.commit('auth/setState', initialState);
            }
        }

        if(domain.backgroundInternalImage)
        {
            var styleElem = document.head.appendChild(document.createElement("style"));
            styleElem.innerHTML = "#main::after {background: url("+domain.backgroundInternalImage+") no-repeat center fixed !important; background-size: cover !important;}";
        }
    },
    mounted() {
        this.$root.$on(
            "refreshRequestManagerNotifications",
            this.refreshRequestManagerNotifications,
        );

        this.initialState = this.$store.getters['auth/getState'];
        const rawDomain = localStorage.getItem('@datadrive:domain');
        let parsedDomain = null;
        if (rawDomain) {
            try { parsedDomain = JSON.parse(rawDomain); } catch (_) { parsedDomain = null; }
        }
        if (parsedDomain && parsedDomain.appName) {
            this.appName = parsedDomain.appName;
        }

        /** ---------- */
        const str = this.$route.fullPath;
        const checkModule = str.indexOf("backoffice") != -1;
        const acceptedTerms = this.initialState.acceptedTerms;
        const checkValidations =  this.$route.name !== 'login' &&  this.$route.name !== 'resetPassword' &&  this.$route.name !== 'confirmAccountTest' && !checkModule && acceptedTerms;
        const redirectBO = this.$store.getters['auth/getState'].redirectBO;
        if(checkValidations && redirectBO) {
            const getUserModules = this.$store.getters['auth/getState'].modules;
            const checkVrmModule = getUserModules && getUserModules.length ? getUserModules.find(m => m.moduleId == this.$globals.MODULES.VEHICLE_IDENTIFICATION.ID) : null;
            if(checkVrmModule && checkVrmModule.isActive && checkVrmModule.isVisible) {
                this.$router.push({ name: 'backoffice-user-consumption'});
            } else {
                this.$router.push({ name: 'backoffice-entities'});
            }
        }
        /** ---------- */
    },
    beforeDestroy() {
        this.$root.$off(
            "refreshRequestManagerNotifications",
            this.refreshRequestManagerNotifications,
        );
        this.$closePusher()
        this.$closeBeams();
    }
}
</script>

<style lang="scss">
@import '@sass/app.scss';

.terms-and-conditions-first-login {
    margin-left: 0px !important;
    .mdc-page-body {
        display: flex !important;
        justify-content: center !important;
    }
}

#invisible-button-app {
    position: absolute;
    z-index: -999;
}

#content-backoffice {
    @media (max-width: 1900px) {
        overflow-y: auto;
    }
}

.chat-fab {
    position: fixed;
    bottom: 40px;
    right: 3%;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease-in-out;
    z-index: 1000;

    @media (min-width: 1921px) {
        right: calc((100vw - 1920px) / 2 + 50px);
    }
}
</style>
